/* El buscador del hub: una sola caja.
 *
 * ── POR QUÉ NO CLASIFICA LA CONSULTA ──────────────────────────────────────
 *
 * La tentación es adivinar qué escribió la persona —«esto son cuatro dígitos,
 * es un interno»— y buscar sólo ahí. No se hace, y por dos motivos.
 *
 * EL PRIMERO ES QUE LOS ESPACIOS SE PISAN. Un interno tiene 3 o 4 dígitos y un
 * DNI tiene 7 u 8, pero un legajo tiene 3 o 4 también: `774` es un interno y
 * puede ser el legajo de alguien. Un clasificador que acierta el 90% de las
 * veces contesta «no encontré nada» el otro 10%, y quien busca **ya sabe que
 * eso existe** — así que concluye que el sistema no lo tiene. Es el peor no
 * que puede dar un buscador, y es la misma regla que ya está escrita en el
 * padrón: la búsqueda le gana al filtro.
 *
 * EL SEGUNDO ES QUE NO HACE FALTA. Los padrones ya están en memoria —el
 * armazón los tiene cargados para el menú—, así que recorrer los tres cuesta
 * lo mismo que recorrer uno. No hay nada que optimizar y sí algo que perder.
 *
 * ── Y NUNCA ELIGE ─────────────────────────────────────────────────────────
 *
 * **430 internos existen en las dos tablas.** Escribir `774` matchea el
 * tractor AH989GD y el furgón GXQ454, y los dos se muestran. Quedarse con el
 * más probable llevaría a la ficha equivocada la mitad de las veces, con total
 * confianza y sin fallar en ningún lado. Misma decisión que `patentes.py`.
 *
 * Es puro: entra texto y los padrones, sale qué mostrar. Se prueba en una
 * tabla, sin red.
 */

import { identidad, numero, patente } from "./unidad";

/* Con una sola letra matchea medio padrón y la lista es ruido. Dos ya
 * discrimina, y con dos dígitos («77») el que está tipeando un interno ve
 * aparecer los candidatos antes de terminar. */
export const MINIMO = 2;

/* Cuántos se muestran por grupo. **Lo que queda afuera se CUENTA**, nunca se
 * corta en silencio: una lista recortada sin avisar se lee como la lista
 * completa, y el que buscaba lo que quedó abajo concluye que no está. */
export const TOPE = 6;

export const VACIO = {
  hay: false,
  corto: false,
  unidades: [],
  personas: [],
  historicas: [],
  demas: { unidades: 0, personas: 0, historicas: 0 },
};

/* Sin acentos y en mayúsculas. `GONZALEZ` y `González` son la misma persona, y
 * comparando el texto crudo el segundo no aparece nunca — que es peor que no
 * buscar, porque la pantalla dice que no existe. */
export function normalizar(t) {
  return String(t ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase();
}

const nombreDe = (p) =>
  normalizar(
    [p?.apellido, p?.nombres].filter(Boolean).join(" ") || p?.nombre || "",
  );

/* El nombre matchea si TODAS las palabras tipeadas están, en cualquier orden, y
 * no como un solo pedazo contiguo. «Cardozo Julio» tiene que encontrar a
 * «CARDOZO LEGUIZAMON JULIO CESAR» —apellido compuesto de por medio—; con un
 * `includes` del texto entero no lo encontraba, y quien lo busca por nombre y
 * apellido concluye que no está cargado. Es el mismo «no» peor que un buscador
 * puede dar. Una sola palabra sigue funcionando igual. */
function coincideNombre(nom, tokens) {
  return !!nom && tokens.every((tok) => nom.includes(tok));
}

/* Una unidad matchea por interno o por patente, y se guarda POR CUÁL:
 * sin eso, un resultado que salió por la chapa se lee como si el número
 * tipeado fuera su interno. */
function matchUnidad(fila, tipo, q, qDigitos, qChapa) {
  const int = numero(fila?.interno);
  const chapa = patente(fila?.dominio);

  /* EXACTO ES QUE LO TIPEADO SEA ESE INTERNO, no que coincidan los dígitos.
   * Aldo, 29/10, ticket 559: buscando «AF 901 BT» los dígitos `901` daban
   * exacto con el furgón 901 (FOF215), que quedaba primero —el del Enter—
   * por delante del playo cuya patente era justo ésa; y `143` y `143-P` eran
   * el mismo «143». Se sigue encontrando por los dígitos (no se clasifica la
   * consulta), pero es exacto sólo si el texto entero es el interno: `774`,
   * `F774` para un furgón, `143-P` / `143P` para el playo. */
  if (qDigitos && int) {
    const id = identidad(fila?.interno).replace(/-/g, "");
    const exacto =
      qChapa === id || (tipo === "furgon" && qChapa === `F${id}`);
    if (exacto) return { por: "interno", exacto: true };
    if (int.startsWith(qDigitos)) {
      /* Una patente exacta le gana a un interno que sólo comparte dígitos. */
      if (qChapa && chapa === qChapa) return { por: "patente", exacto: true };
      return { por: "interno", exacto: false };
    }
  }
  if (qChapa && chapa && chapa.includes(qChapa)) {
    return { por: "patente", exacto: chapa === qChapa };
  }
  /* El texto libre también busca en la marca: «scania» es una forma legítima
   * de acotar, y no cuesta nada. Nunca es exacta. */
  if (q.length >= 3 && normalizar(fila?.marca).includes(q)) {
    return { por: "marca", exacto: false };
  }
  return null;
}

/* Primero lo exacto, después por interno, y dentro de eso los tractores.
 *
 * El orden importa porque el primer resultado es el que se elige con Enter:
 * un `774` que devuelva primero una coincidencia parcial —el 7740— llevaría a
 * la unidad equivocada al que sólo apretó Enter.
 */
function ordenar(a, b) {
  if (a.exacto !== b.exacto) return a.exacto ? -1 : 1;
  if (a.por !== b.por) return a.por === "interno" ? -1 : 1;
  if (a.tipo !== b.tipo) return a.tipo === "tractor" ? -1 : 1;
  return String(a.interno).localeCompare(String(b.interno), "es", {
    numeric: true,
  });
}

export function buscar(texto, datos = {}) {
  const q = normalizar(texto);
  if (q.length < MINIMO) return { ...VACIO, corto: q.length > 0 };

  const qDigitos = q.replace(/\D/g, "") || null;
  const qChapa = q.replace(/[^A-Z0-9]/g, "") || null;
  /* Las palabras del texto, para el match de nombre por tokens. */
  const qTokens = q.split(/\s+/).filter(Boolean);

  const unidades = [];
  for (const [tipo, lista] of [
    ["tractor", datos.tractores],
    ["furgon", datos.furgones],
  ]) {
    for (const f of lista || []) {
      const m = matchUnidad(f, tipo, q, qDigitos, qChapa);
      if (!m) continue;
      unidades.push({
        tipo,
        /* Con el sufijo (`143-P`): es lo que arma la ruta a la ficha, y con
         * el número pelado el playo AF901BT abría el furgón 143 (ticket 559). */
        interno: identidad(f.interno),
        dominio: patente(f.dominio),
        /* `activo === false` es una baja; `undefined` es que esa tabla no
         * tiene la columna, que NO es lo mismo. Se pasa tal cual y decide la
         * pantalla. */
        activo: f.activo,
        marca: f.marca || "",
        // El CUIT tal cual: la sigla y el color los pone la pantalla con
        // `shared/empresa`, la misma traducción que la ficha.
        empresa: f.empresa || "",
        ...m,
      });
    }
  }
  unidades.sort(ordenar);

  const personas = [];
  for (const p of datos.personas || []) {
    const nom = nombreDe(p);
    const dni = String(p?.dni ?? "").replace(/\D/g, "");
    const legajo = String(p?.legajo ?? "").replace(/\D/g, "");
    let por = null;
    if (coincideNombre(nom, qTokens)) por = "nombre";
    else if (qDigitos && dni && dni === qDigitos) por = "dni";
    else if (qDigitos && legajo && legajo === qDigitos) por = "legajo";
    if (!por) continue;
    personas.push({
      dni: p.dni,
      nombre: [p.apellido, p.nombres].filter(Boolean).join(", ") || p.nombre,
      legajo: p.legajo || null,
      empresa: p.empresa || "",
      estado: p.estado,
      /* LOS QUE YA NO TRABAJAN TAMBIÉN SE ENCUENTRAN (Igna, 02/10: «si busco
         a uno que no está más debería traerlo»), marcados y DESPUÉS de los
         activos: el que busca «Pérez» casi siempre busca al de hoy. */
      baja: String(p.estado).toLowerCase() !== "true",
      por,
      exacto: por !== "nombre" || nom === q,
    });
  }
  personas.sort((a, b) =>
    a.exacto !== b.exacto
      ? a.exacto
        ? -1
        : 1
      : a.baja !== b.baja
        ? a.baja
          ? 1
          : -1
        : String(a.nombre).localeCompare(String(b.nombre), "es"),
  );

  /* ── EL ARCHIVO, QUE SE ENCUENTRA PERO NO SE MEZCLA ─────────────────────
   *
   * Ticket 155 punto 4. Igna, 30/08: «que los puedan buscar de esa manera y
   * traiga los datos de la planilla. Pero a la vista no quedan».
   *
   * Grupo aparte y no una unidad más de la lista, porque son cosas distintas:
   * las de arriba están en el padrón y se puede entrar a su ficha; éstas son
   * camiones que ya no son nuestros y de los que sólo queda lo que decía la
   * planilla. Mezcladas, alguien busca el 92, encuentra dos, y el que abre el
   * equivocado no tiene forma de saberlo.
   *
   * SÓLO POR INTERNO O POR PATENTE, nunca por marca: 96 de las 99 son SCANIA o
   * IVECO y tipear «SCA» taparía el resultado vivo con el archivo entero. Ésta
   * es la diferencia con las unidades del padrón, donde la marca sí busca.
   *
   * Y EL INTERNO DEVUELVE VARIAS A PROPÓSITO: se recicla, así que el 92 puede
   * ser tres camiones distintos a lo largo de los años. Quedarse con uno sería
   * elegir por el que busca sin decirle que había otros. */
  const historicas = [];
  for (const h of datos.historicas || []) {
    const dom = String(h?.dominio ?? "").replace(/[^A-Z0-9]/gi, "").toUpperCase();
    const int = String(h?.interno_planilla ?? "").trim();
    let por = null;
    if (qChapa && dom && dom === qChapa) por = "patente";
    else if (qDigitos && int && int === qDigitos) por = "interno";
    if (!por) continue;
    historicas.push({
      dominio: h.dominio,
      interno: int || null,
      empresa: h.empresa || "",
      marca: h.marca || "",
      modelo: h.modelo || "",
      anio: h.anio ?? null,
      /* Verbatim, como en la base: no se traduce «SSEG» a nada nuestro. */
      estado_planilla: h.estado_planilla || "",
      por,
    });
  }
  historicas.sort((a, b) => String(a.dominio).localeCompare(String(b.dominio)));

  return {
    hay: unidades.length > 0 || personas.length > 0 || historicas.length > 0,
    corto: false,
    unidades: unidades.slice(0, TOPE),
    personas: personas.slice(0, TOPE),
    historicas: historicas.slice(0, TOPE),
    demas: {
      unidades: Math.max(0, unidades.length - TOPE),
      personas: Math.max(0, personas.length - TOPE),
      historicas: Math.max(0, historicas.length - TOPE),
    },
  };
}

/* Cómo se explica de dónde salió cada resultado. Sin esto, una unidad que
 * apareció por la chapa se lee como si el número tipeado fuera su interno. */
export const POR = {
  interno: "por el interno",
  patente: "por la patente",
  marca: "por la marca",
  nombre: "por el nombre",
  dni: "por el DNI",
  legajo: "por el legajo",
};
